import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { FileSpreadsheet, BarChart3, Download, CheckCircle, Clock, AlertTriangle, Users, DollarSign, Calendar } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function getMonday(offset = 0) {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) + (offset * 7);
  const mon = new Date(d.setDate(diff));
  return mon.toISOString().split('T')[0];
}

export default function TimesheetsPage() {
  const { user } = useAuth();
  const isDirector = user?.role === 'director';
  const [tab, setTab] = useState('reports');
  const [timesheets, setTimesheets] = useState([]);
  const [selectedTs, setSelectedTs] = useState(null);
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [labourReport, setLabourReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [reportStart, setReportStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; });
  const [reportEnd, setReportEnd] = useState(new Date().toISOString().split('T')[0]);
  const [healing, setHealing] = useState(false);

  const fetchTimesheets = useCallback(async () => {
    try { const { data } = await api.get('/staff/timesheets'); setTimesheets(data); } catch (err) { console.error('[TimesheetsPage.js] error:', err); }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      const [att, lab] = await Promise.all([
        api.get(`/staff/reports/attendance?start_date=${reportStart}&end_date=${reportEnd}`),
        api.get(`/staff/reports/labour?start_date=${reportStart}&end_date=${reportEnd}`)
      ]);
      setAttendanceReport(att.data);
      setLabourReport(lab.data);
    } catch (err) { console.error('[TimesheetsPage.js] error:', err); }
  }, [reportStart, reportEnd]);

  useEffect(() => { fetchTimesheets(); }, [fetchTimesheets]);
  useEffect(() => { if (tab === 'reports') fetchReports(); }, [tab, fetchReports]);

  const healOpenShifts = async () => {
    setHealing(true);
    try {
      const { data } = await api.post('/staff/clock/heal-open-records');
      toast.success(data.message || `Healed ${data.healed_count} open shift(s)`);
      fetchReports();
    } catch (err) { toast.error(err.response?.data?.detail || 'Heal failed'); }
    finally { setHealing(false); }
  };

  const generateTimesheet = async (periodType = 'weekly') => {
    setGenerating(true);
    try {
      const weekStart = getMonday(-1);
      const { data } = await api.post('/staff/timesheets/generate', { week_start: weekStart, period_type: periodType });
      toast.success(`Timesheet generated: ${data.total_staff} staff, ${data.total_hours}h`);
      fetchTimesheets();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setGenerating(false); }
  };

  const approveTimesheet = async (id) => {
    try {
      await api.put(`/staff/timesheets/${id}/approve`);
      toast.success('Timesheet approved');
      fetchTimesheets();
      if (selectedTs?.id === id) { const { data } = await api.get(`/staff/timesheets/${id}`); setSelectedTs(data); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const downloadCsv = (tsId) => {
    window.open(`${API_BASE}/api/staff/reports/export-csv?ts_id=${tsId}`, '_blank');
  };

  const downloadBankCsv = (tsId) => {
    window.open(`${API_BASE}/api/staff/timesheets/${tsId}/bank-csv`, '_blank');
  };

  const markPaid = async (id) => {
    const ref = window.prompt('Payment reference (e.g. bank batch ID, optional):', '');
    if (ref === null) return;
    try {
      await api.put(`/staff/timesheets/${id}/mark-paid`, { payment_reference: ref || '', notes: '' });
      toast.success('Timesheet marked as paid. Payroll history updated.');
      fetchTimesheets();
      if (selectedTs?.id === id) { const { data } = await api.get(`/staff/timesheets/${id}`); setSelectedTs(data); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const [payslipData, setPayslipData] = useState(null);
  const viewPayslip = async (tsId, userId) => {
    try {
      const { data } = await api.get(`/staff/timesheets/${tsId}/payslip/${userId}`);
      setPayslipData(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const tabs = [
    { key: 'timesheets', label: 'Timesheets', icon: FileSpreadsheet },
    { key: 'reports', label: 'Reports', icon: BarChart3 },
  ];

  return (
    <div className="animate-fadeIn">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1.5">
          {tabs.map(t => (
            <Button key={t.key} size="sm" variant={tab === t.key ? 'default' : 'ghost'} onClick={() => setTab(t.key)}
              className={`h-8 rounded-sm font-sans text-xs ${tab === t.key ? 'bg-gold/10 text-gold border border-gold/20' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`ts-tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5 mr-1.5" /> {t.label}
            </Button>
          ))}
        </div>
        {tab === 'timesheets' && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => generateTimesheet('weekly')} disabled={generating} className="h-8 bg-gold text-black font-bold rounded-sm font-sans text-xs" data-testid="generate-weekly">
              {generating ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <><FileSpreadsheet className="w-3 h-3 mr-1" /> Generate Weekly</>}
            </Button>
            <Button size="sm" onClick={() => generateTimesheet('fortnightly')} disabled={generating} className="h-8 bg-white/5 text-zinc-300 border border-white/10 rounded-sm font-sans text-xs" data-testid="generate-fortnightly">
              Generate Fortnightly
            </Button>
          </div>
        )}
      </div>

      {/* TIMESHEETS TAB */}
      {tab === 'timesheets' && (
        <div className="space-y-3">
          {timesheets.length === 0 ? (
            <div className="text-center py-12">
              <FileSpreadsheet className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 font-sans">No timesheets generated yet</p>
              <p className="text-zinc-600 font-sans text-xs mt-1">Click "Generate Weekly" to create a timesheet from last week's clock records</p>
            </div>
          ) : timesheets.map(ts => (
            <div key={ts.id} className="p-4 bg-white/[0.03] border border-white/5 rounded-sm card-hover cursor-pointer" onClick={() => setSelectedTs(ts)} data-testid={`timesheet-${ts.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-white font-sans text-sm font-medium">{ts.week_start} — {ts.week_end}</span>
                    <Badge className={`${ts.status === 'paid' ? 'bg-blue-500/20 text-blue-400' : ts.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'} border-0 font-sans text-[10px]`}>{ts.status}</Badge>
                    <Badge className="bg-zinc-500/20 text-zinc-400 border-0 font-sans text-[10px]">{ts.period_type}</Badge>
                  </div>
                  <div className="flex gap-3 text-xs font-sans text-zinc-500 flex-wrap">
                    <span><Users className="w-3 h-3 inline mr-1" />{ts.total_staff} staff</span>
                    <span><Clock className="w-3 h-3 inline mr-1" />{ts.total_hours}h</span>
                    <span><DollarSign className="w-3 h-3 inline mr-1" />${ts.total_gross_pay} gross</span>
                    {ts.total_tips > 0 && <span className="text-emerald-500">+${ts.total_tips} tips</span>}
                    <span className="text-zinc-400">${ts.total_net_pay || ts.total_gross_pay} net</span>
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {ts.status === 'draft' && (
                    <Button size="sm" onClick={e => { e.stopPropagation(); approveTimesheet(ts.id); }} className="h-7 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 rounded-sm font-sans text-xs" data-testid={`approve-ts-${ts.id}`}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Approve
                    </Button>
                  )}
                  {ts.status === 'approved' && isDirector && (
                    <Button size="sm" onClick={e => { e.stopPropagation(); markPaid(ts.id); }} className="h-7 bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 rounded-sm font-sans text-xs" data-testid={`mark-paid-${ts.id}`}>
                      <DollarSign className="w-3 h-3 mr-1" /> Mark Paid
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); downloadCsv(ts.id); }} className="h-7 text-zinc-400 hover:text-white font-sans text-xs" data-testid={`download-ts-${ts.id}`}>
                    <Download className="w-3 h-3 mr-1" /> CSV
                  </Button>
                  {ts.status !== 'draft' && isDirector && (
                    <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); downloadBankCsv(ts.id); }} className="h-7 text-zinc-400 hover:text-blue-400 font-sans text-xs" data-testid={`bank-csv-${ts.id}`}>
                      Bank CSV
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* REPORTS TAB */}
      {tab === 'reports' && (
        <div className="space-y-6">
          <div className="flex gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">From</Label>
              <Input type="date" value={reportStart} onChange={e => setReportStart(e.target.value)} className="bg-transparent border border-white/10 text-white font-sans text-sm w-40" data-testid="report-start" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">To</Label>
              <Input type="date" value={reportEnd} onChange={e => setReportEnd(e.target.value)} className="bg-transparent border border-white/10 text-white font-sans text-sm w-40" data-testid="report-end" />
            </div>
            <Button size="sm" onClick={fetchReports} className="h-9 bg-gold/10 text-gold border border-gold/20 rounded-sm font-sans text-xs" data-testid="refresh-reports">Refresh</Button>
            <Button size="sm" onClick={healOpenShifts} disabled={healing} title="Closes shifts where staff forgot to clock out (stuck at 0 hours) and estimates their hours to the 1am shift end" className="h-9 bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 rounded-sm font-sans text-xs" data-testid="heal-open-shifts">
              {healing ? 'Fixing…' : 'Fix Missed Clock-Outs'}
            </Button>
          </div>

          {/* Summary Cards */}
          {attendanceReport && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <SummaryCard label="Total Staff" value={attendanceReport.total_staff} icon={Users} />
              <SummaryCard label="Total Hours" value={`${attendanceReport.total_hours}h`} icon={Clock} />
              <SummaryCard label="Overtime" value={`${attendanceReport.total_overtime}h`} icon={AlertTriangle} color="amber" />
              <SummaryCard label="Late Instances" value={attendanceReport.total_late_instances} icon={AlertTriangle} color="red" />
              <SummaryCard label="Absences" value={attendanceReport.total_absences} icon={Calendar} color="red" />
            </div>
          )}

          {/* Attendance Table */}
          {attendanceReport?.staff?.length > 0 && (
            <div className="bg-white/[0.03] border border-white/5 rounded-sm overflow-hidden">
              <div className="p-3 border-b border-white/5">
                <h3 className="text-sm font-sans text-zinc-400 font-medium">Attendance Report</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-sans">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left p-3 text-zinc-500 font-medium">Staff</th>
                      <th className="text-center p-3 text-zinc-500 font-medium">Scheduled</th>
                      <th className="text-center p-3 text-zinc-500 font-medium">Present</th>
                      <th className="text-center p-3 text-zinc-500 font-medium">Absent</th>
                      <th className="text-center p-3 text-zinc-500 font-medium">Hours</th>
                      <th className="text-center p-3 text-zinc-500 font-medium">OT</th>
                      <th className="text-center p-3 text-zinc-500 font-medium">Late</th>
                      <th className="text-center p-3 text-zinc-500 font-medium">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceReport.staff.map(s => (
                      <tr key={s.user_id} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`att-row-${s.user_id}`}>
                        <td className="p-3"><span className="text-white">{s.user_name}</span> <span className="text-zinc-600 capitalize">({s.role})</span></td>
                        <td className="text-center p-3 text-zinc-400">{s.scheduled_shifts}</td>
                        <td className="text-center p-3 text-emerald-400">{s.days_present}</td>
                        <td className="text-center p-3 text-red-400">{s.absent_days}</td>
                        <td className="text-center p-3 text-white">{s.total_hours}h</td>
                        <td className="text-center p-3 text-amber-400">{s.overtime_hours > 0 ? `${s.overtime_hours}h` : '-'}</td>
                        <td className="text-center p-3 text-red-400">{s.late_count > 0 ? s.late_count : '-'}</td>
                        <td className="text-center p-3 text-zinc-400">{s.attendance_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Labour Cost */}
          {labourReport && (
            <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4">
              <h3 className="text-sm font-sans text-zinc-400 font-medium mb-3">Labour Cost Summary</h3>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="text-center"><p className="text-white font-sans text-lg font-semibold">{labourReport.total_hours}h</p><p className="text-zinc-500 text-[10px] uppercase">Total Hours</p></div>
                <div className="text-center"><p className="text-amber-400 font-sans text-lg font-semibold">{labourReport.total_overtime}h</p><p className="text-zinc-500 text-[10px] uppercase">Overtime</p></div>
                <div className="text-center"><p className="text-gold font-sans text-lg font-semibold">${labourReport.total_cost}</p><p className="text-zinc-500 text-[10px] uppercase">Total Cost</p></div>
                <div className="text-center"><p className="text-zinc-300 font-sans text-lg font-semibold">{labourReport.avg_daily_hours}h</p><p className="text-zinc-500 text-[10px] uppercase">Avg Daily</p></div>
              </div>
              {labourReport.daily?.length > 0 && (
                <div className="space-y-1">
                  {labourReport.daily.map(d => (
                    <div key={d.date} className="flex items-center justify-between p-2 bg-white/[0.02] rounded-sm text-xs font-sans">
                      <span className="text-zinc-400 w-24">{d.date}</span>
                      <span className="text-white">{d.staff_count} staff</span>
                      <span className="text-white">{d.hours}h</span>
                      <span className="text-amber-400">{d.overtime > 0 ? `${d.overtime}h OT` : '-'}</span>
                      <span className="text-gold">${d.cost}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Timesheet Detail Dialog */}
      <Dialog open={!!selectedTs} onOpenChange={v => { if (!v) setSelectedTs(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-3xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
          {selectedTs && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-white flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-gold" /> Timesheet: {selectedTs.week_start} — {selectedTs.week_end}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge className={`${selectedTs.status === 'paid' ? 'bg-blue-500/20 text-blue-400' : selectedTs.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'} border-0 font-sans text-xs`}>{selectedTs.status}</Badge>
                  {selectedTs.abn_mode && <Badge className="bg-blue-500/20 text-blue-400 border-0 font-sans text-[10px]" data-testid="abn-badge">ABN — no tax / super</Badge>}
                  <span className="text-zinc-500 font-sans text-xs">{selectedTs.total_staff} staff | {selectedTs.total_hours}h | ${selectedTs.total_net_pay || selectedTs.total_gross_pay} net</span>
                  <div className="ml-auto flex gap-1.5 flex-wrap">
                    {selectedTs.status === 'draft' && (
                      <Button size="sm" onClick={() => approveTimesheet(selectedTs.id)} className="h-7 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-sm font-sans text-xs" data-testid="dialog-approve">
                        <CheckCircle className="w-3 h-3 mr-1" /> Approve
                      </Button>
                    )}
                    {selectedTs.status === 'approved' && isDirector && (
                      <Button size="sm" onClick={() => markPaid(selectedTs.id)} className="h-7 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-sm font-sans text-xs" data-testid="dialog-mark-paid">
                        <DollarSign className="w-3 h-3 mr-1" /> Mark Paid
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => downloadCsv(selectedTs.id)} className="h-7 text-zinc-400 hover:text-white font-sans text-xs">
                      <Download className="w-3 h-3 mr-1" /> Payroll CSV
                    </Button>
                    {selectedTs.status !== 'draft' && isDirector && (
                      <Button size="sm" variant="ghost" onClick={() => downloadBankCsv(selectedTs.id)} className="h-7 text-zinc-400 hover:text-blue-400 font-sans text-xs">
                        Bank CSV
                      </Button>
                    )}
                  </div>
                </div>

                {/* Totals strip */}
                <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
                  <Stat label="Wages" value={`$${selectedTs.total_wages || 0}`} />
                  <Stat label="Tips" value={`$${selectedTs.total_tips || 0}`} accent="emerald" />
                  <Stat label="Gross" value={`$${selectedTs.total_gross_pay || 0}`} />
                  {!selectedTs.abn_mode && <Stat label="Tax" value={`$${selectedTs.total_tax || 0}`} accent="red" />}
                  {!selectedTs.abn_mode && <Stat label="Super" value={`$${selectedTs.total_super || 0}`} accent="amber" />}
                  <Stat label="Net Pay" value={`$${selectedTs.total_net_pay || 0}`} accent="gold" />
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs font-sans">
                    <thead>
                      <tr className="border-b border-white/5">
                        <th className="text-left p-2 text-zinc-500">Staff</th>
                        <th className="text-center p-2 text-zinc-500">Hours</th>
                        <th className="text-right p-2 text-zinc-500">Wages</th>
                        <th className="text-right p-2 text-zinc-500">Tips</th>
                        <th className="text-right p-2 text-zinc-500">Gross</th>
                        {!selectedTs.abn_mode && <th className="text-right p-2 text-zinc-500">Tax</th>}
                        {!selectedTs.abn_mode && <th className="text-right p-2 text-zinc-500">Super</th>}
                        <th className="text-right p-2 text-zinc-500">Net</th>
                        <th className="text-center p-2 text-zinc-500"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTs.entries?.map(e => (
                        <tr key={e.user_id} className="border-b border-white/[0.03]" data-testid={`payslip-row-${e.user_id}`}>
                          <td className="p-2 text-white">{e.user_name} <span className="text-zinc-600 capitalize">({e.role})</span></td>
                          <td className="text-center p-2 text-zinc-300">{e.total_hours}h{e.overtime_hours > 0 ? <span className="text-amber-400 text-[10px]"> +{e.overtime_hours}OT</span> : ''}</td>
                          <td className="text-right p-2 text-zinc-300">${(e.gross_wages || 0).toFixed(2)}</td>
                          <td className="text-right p-2 text-emerald-400">{e.tip_share > 0 ? `$${e.tip_share.toFixed(2)}` : '—'}</td>
                          <td className="text-right p-2 text-white">${(e.gross_pay || 0).toFixed(2)}</td>
                          {!selectedTs.abn_mode && <td className="text-right p-2 text-red-400">${(e.tax_amount || 0).toFixed(2)}</td>}
                          {!selectedTs.abn_mode && <td className="text-right p-2 text-amber-400">${(e.super_amount || 0).toFixed(2)}</td>}
                          <td className="text-right p-2 text-gold font-medium">${(e.net_pay || 0).toFixed(2)}</td>
                          <td className="text-center p-2"><Button size="sm" variant="ghost" onClick={() => viewPayslip(selectedTs.id, e.user_id)} className="h-6 text-zinc-500 hover:text-gold text-[10px] font-sans px-1.5" data-testid={`view-payslip-${e.user_id}`}>Payslip</Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* PAYSLIP DIALOG */}
      <Dialog open={!!payslipData} onOpenChange={v => { if (!v) setPayslipData(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto print:bg-white print:text-black">
          <DialogHeader>
            <DialogTitle className="sr-only">Payslip{payslipData ? ` for ${payslipData.entry.user_name}` : ''}</DialogTitle>
          </DialogHeader>
          {payslipData && (
            <div className="space-y-3" data-testid="payslip-content">
              <div className="text-center border-b border-white/10 pb-3">
                <h2 className="text-xl font-serif text-gold">{payslipData.venue.name}</h2>
                <p className="text-xs text-zinc-500 font-sans">{payslipData.venue.address}</p>
                <p className="text-xs text-zinc-500 font-sans">PAYSLIP</p>
              </div>
              <div className="text-xs font-sans space-y-1">
                <Row k="Employee" v={payslipData.entry.user_name} />
                <Row k="Role" v={`${payslipData.entry.role} (${payslipData.entry.employment_type})`} />
                <Row k="Pay Period" v={`${payslipData.period.start} → ${payslipData.period.end}`} />
                <Row k="Status" v={payslipData.status} />
                {payslipData.paid_at && <Row k="Paid At" v={new Date(payslipData.paid_at).toLocaleDateString()} />}
                {payslipData.payment_reference && <Row k="Reference" v={payslipData.payment_reference} />}
              </div>
              <div className="border-t border-white/10 pt-2">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans mb-1">Earnings</p>
                <Row k={`Regular hours (${payslipData.entry.regular_hours}h × $${payslipData.entry.hourly_rate})`} v={`$${(payslipData.entry.regular_hours * payslipData.entry.hourly_rate).toFixed(2)}`} />
                {payslipData.entry.overtime_hours > 0 && (
                  <Row k={`Overtime (${payslipData.entry.overtime_hours}h × $${(payslipData.entry.hourly_rate * 1.5).toFixed(2)})`} v={`$${(payslipData.entry.overtime_hours * payslipData.entry.hourly_rate * 1.5).toFixed(2)}`} />
                )}
                {payslipData.entry.tip_share > 0 && (
                  <Row k="Tip share (pooled)" v={`$${payslipData.entry.tip_share.toFixed(2)}`} accent="emerald" />
                )}
                <Row k="Gross Pay" v={`$${payslipData.entry.gross_pay.toFixed(2)}`} bold />
              </div>
              {payslipData.abn_mode ? (
                <div className="border-t border-white/10 pt-2">
                  <p className="text-[11px] text-zinc-400 font-sans" data-testid="abn-payslip-note">ABN contractor — paid gross in full. No PAYG tax withheld, no superannuation. Contractor manages their own tax obligations.</p>
                </div>
              ) : (
                <div className="border-t border-white/10 pt-2">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans mb-1">Deductions</p>
                  <Row k="Tax (PAYG)" v={`-$${payslipData.entry.tax_amount.toFixed(2)}`} accent="red" />
                </div>
              )}
              <div className="border-t border-gold/30 pt-2 bg-gold/5 -mx-6 px-6 py-2">
                <Row k="NET PAY" v={`$${payslipData.entry.net_pay.toFixed(2)}`} bold large />
              </div>
              {!payslipData.abn_mode && (
                <div className="border-t border-white/10 pt-2">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans mb-1">Employer-paid Superannuation</p>
                  <Row k={`Super (${payslipData.super_rate}% on ordinary hours)`} v={`$${payslipData.entry.super_amount.toFixed(2)}`} accent="amber" />
                  {payslipData.entry.superannuation_fund && <Row k="Fund" v={payslipData.entry.superannuation_fund} />}
                </div>
              )}
              {(payslipData.entry.bank_bsb || payslipData.entry.bank_account) && (
                <div className="border-t border-white/10 pt-2">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans mb-1">Payment Details</p>
                  <Row k="BSB" v={payslipData.entry.bank_bsb || '—'} />
                  <Row k="Account" v={payslipData.entry.bank_account || '—'} />
                </div>
              )}
              <Button onClick={() => window.print()} className="w-full bg-gold text-black font-bold rounded-sm font-sans text-sm h-9 print:hidden" data-testid="print-payslip">Print Payslip</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v, accent, bold, large }) {
  const colorClass = accent === 'red' ? 'text-red-400' : accent === 'emerald' ? 'text-emerald-400' : accent === 'amber' ? 'text-amber-400' : 'text-white';
  const sizeClass = large ? 'text-lg' : '';
  return (
    <div className="flex justify-between items-center">
      <span className="text-zinc-400 font-sans text-xs">{k}</span>
      <span className={`font-sans ${colorClass} ${sizeClass} ${bold ? 'font-bold' : ''}`}>{v}</span>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const colorClass = accent === 'red' ? 'text-red-400' : accent === 'emerald' ? 'text-emerald-400' : accent === 'amber' ? 'text-amber-400' : accent === 'gold' ? 'text-gold' : 'text-white';
  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-sm p-2 text-center">
      <p className={`font-sans ${colorClass} text-sm font-semibold`}>{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans">{label}</p>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color = 'zinc' }) {
  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-sm p-3 text-center">
      <Icon className={`w-4 h-4 text-${color}-400 mx-auto mb-1`} />
      <p className="text-lg font-sans text-white font-semibold">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans">{label}</p>
    </div>
  );
}
